import {
  fetchConnectivityTxTimerValue,
  fetchDataIPV4AddressValue,
  fetchDataPortValue,
  fetchFixedStationTransmitterReportIntervalValue,
  fetchFixedStationVoiceIPV4Address,
  fetchFixedStationVoterReportingPeriodValue,
  fetchFSHeartbeatPeriodValue,
  fetchHostControlIPV4Address,
  fetchHostControlPort,
  fetchHostHeartbeatPeriodValue,
  fetchHostVoiceIPV4Address,
  getUniqueCombination,
} from ".";
import { fieldNameMapping } from "../constants";
import { Page } from "../translations/types";
import { ADD_FORM_FIELDS_TRANSLATIONS } from "../translations/util";

export const handleBulkCTCnCDCvalues = (
  field?: string,
  ctcvalue?: string,
  cdcValue?: string,
  isChecked?: boolean
) => {
  if (ctcvalue === "0" && cdcValue === "0" && !isChecked) return 1;
  if (isChecked) return 1;
  if (field === "ctc" && cdcValue === "" && ctcvalue !== "") return 1;
  if (field === "cdc" && ctcvalue === "" && cdcValue !== "") return 1;
  return null;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
let finalFile: { [key: string]: any }[] = [];
let filewitherror: { [key: string]: any }[] = [];

// Organisation
interface IOrgBulkEntryType {
  "ORGANISATION-ID": string;
  "ORGANISATION-NAME": string;
}

const verifyOrganisationEntries = (field: string, value: string) => {
  let reason = "";
  const org: { [key: string]: () => string } = {
    "ORGANISATION-ID": function isInputInValid() {
      if (!value || value.length === 0) {
        reason = "organisation-id is a required field";
      }
      return reason;
    },
    "ORGANISATION-NAME": function isInputInValid() {
      if (!value || value.length === 0) {
        reason = "organisation-name is a required field";
      }
      return reason;
    },
  };
  return org[field]();
};

export const handleOrganisationFileValidation = (file: IOrgBulkEntryType[]) => {
  finalFile = [];
  filewitherror = [];
  file.forEach((entry: IOrgBulkEntryType) => {
    if (Object.values(entry).join("").length > 0) {
      const result = Object.keys(entry).map((field: string) =>
        verifyOrganisationEntries(
          field,
          entry[field as keyof IOrgBulkEntryType]
        )
      );

      const errors = result.filter((err) => err.length > 1);
      // check errors
      if (errors.length > 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]} (+ ${
            errors.length - 1
          } other field(s) with errors)`,
        };
        filewitherror.push(newObj);
      } else if (errors.length === 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]}`,
        };
        filewitherror.push(newObj);
      } else {
        finalFile.push(entry);
      }
    }
  });

  return { finalFile, filewitherror };
};

// OTAR Connection
interface IOTARBulkEntryType {
  "CONNECTION-ID": string;
  "KMF-IPV4-ADDR": string;
  "KMF-PORT": number;
  "KMF-RADIO-SET-IDENTIFIER": number;
  "IWF-RADIO-SET-IDENTIFIER": number;
  "MESSAGE-NUMBER-PERIOD": number;
  "PRIVATE-CALL-SLN": number;
  "GROUP-REGROUP-SLN": number;
  "OTAR-REGISTRATION-TIMER": number;
  "OPT-SEND-REKEY-REQ": boolean;
  "OPT-REVERSE-WARM-START": boolean;
  "OPT-DE-REGISTRATION": boolean;
  "OPT-HELLO-MAC-REPLY": boolean;
  "OPT-KEY-ASSIGNMENT": boolean;
  "KEEP-ALIVE-TIMER": number;
}

const verifyOTAREntries = (field: string, value: any) => {
  let reason = "";

  const otar: { [key: string]: () => string } = {
    "CONNECTION-ID": function isInputInValid() {
      if (!value || value.length === 0) {
        reason = "connection-id is a required field";
      }
      return reason;
    },
    "KMF-IPV4-ADDR": function isInputInValid() {
      const regex =
        /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      if (!value || !regex.test(value)) {
        reason = "kmf-ipv4-addr is a required field";
      }
      return reason;
    },
    "KMF-PORT": function isInputInValid() {
      if (
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) < 1024 ||
        parseInt(value, 10) > 65535
      ) {
        reason =
          "kmf-port is a required field and and should be in the range (1024-65535)";
      }
      return reason;
    },
    "KMF-RADIO-SET-IDENTIFIER": function isInputInValid() {
      if (
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) < 1 ||
        parseInt(value, 10) > 9999999
      ) {
        reason =
          "kmf-radio-set-identifier is a required field and and should be in the range (1-9999999)";
      }
      return reason;
    },
    "IWF-RADIO-SET-IDENTIFIER": function isInputInValid() {
      if (
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) < 1 ||
        parseInt(value, 10) > 9999999
      ) {
        reason =
          "iwf-radio-set-identifier is a required field and and should be in the range (1-9999999)";
      }
      return reason;
    },
    "MESSAGE-NUMBER-PERIOD": function isInputInValid() {
      if (
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) < 0 ||
        parseInt(value, 10) > 65535
      ) {
        reason =
          "message-number-period is a required field and and should be in the range (0-65535)";
      }
      return reason;
    },
    "PRIVATE-CALL-SLN": function isInputInValid() {
      if (
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) < 0 ||
        parseInt(value, 10) > 4095
      ) {
        reason =
          "private-call-sln is a required field and and should be in the range (0-4095)";
      }
      return reason;
    },
    "GROUP-REGROUP-SLN": function isInputInValid() {
      if (
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) < 0 ||
        parseInt(value, 10) > 4095
      ) {
        reason =
          "group-regroup-sln is a required field and and should be in the range (0-4095)";
      }
      return reason;
    },
    "OTAR-REGISTRATION-TIMER": function isInputInValid() {
      if (
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) < 1 ||
        parseInt(value, 10) > 72
      ) {
        reason =
          "otar-registration-timer is a required field and and should be in the range (1-72)";
      }
      return reason;
    },
    "OPT-SEND-REKEY-REQ": function isInputInValid() {
      if (!value || !["true", "false"].includes(value.toLowerCase())) {
        reason = "opt-send-rekey-req should be either true or false";
      }
      return reason;
    },
    "OPT-REVERSE-WARM-START": function isInputInValid() {
      if (!value || !["true", "false"].includes(value.toLowerCase())) {
        reason = "opt-reverse-warm-start should be either true or false";
      }
      return reason;
    },
    "OPT-DE-REGISTRATION": function isInputInValid() {
      if (!value || !["true", "false"].includes(value.toLowerCase())) {
        reason = "opt-de-registration should be either true or false";
      }
      return reason;
    },
    "OPT-HELLO-MAC-REPLY": function isInputInValid() {
      if (!value || !["true", "false"].includes(value.toLowerCase())) {
        reason = "opt-hello-mac-reply should be either true or false";
      }
      return reason;
    },
    "OPT-KEY-ASSIGNMENT": function isInputInValid() {
      if (!value || !["true", "false"].includes(value.toLowerCase())) {
        reason = "opt-key-assignment should be either true or false";
      }
      return reason;
    },
    "KEEP-ALIVE-TIMER": function isInputInValid() {
      if (
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) < 0 ||
        parseInt(value, 10) > 300
      ) {
        reason =
          "keep-alive-timer is a required field and and should be in the range (0-300)";
      }
      return reason;
    },
  };
  return otar[field]();
};

export const handleOTARConnectionFileValidation = (
  file: IOTARBulkEntryType[]
) => {
  finalFile = [];
  filewitherror = [];

  file.forEach((entry: IOTARBulkEntryType) => {
    if (Object.values(entry).join("").length > 0) {
      const result = Object.keys(entry).map((field: string) =>
        verifyOTAREntries(field, entry[field as keyof IOTARBulkEntryType])
      );

      const errors = result.filter((err) => err.length > 1);
      // check errors
      if (errors.length > 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]} (+ ${
            errors.length - 1
          } other field(s) with errors)`,
        };
        filewitherror.push(newObj);
      } else if (errors.length === 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]}`,
        };
        filewitherror.push(newObj);
      } else {
        finalFile.push(entry);
      }
    }
  });
  return { finalFile, filewitherror };
};

// Organisation Translation
interface IOrgTranslationBulkEntryType {
  "PRIORITY-INCOMING": string;
  "EMERGENCY-FLAG-INCOMING": string;
  "EMERGENCY-FLAG-OUTGOING": string;
  "PRIORITY-OUTGOING-VALUE": string;
}

const verifyOrgTranslationEntries = (field: string, value: string) => {
  let reason = "";
  const org: { [key: string]: () => string } = {
    "PRIORITY-INCOMING": function isInputInValid() {
      if (
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) < Number.MIN_SAFE_INTEGER ||
        parseInt(value, 10) > 2147483647
      ) {
        reason =
          "priority-incoming is a required field and should be less than 2147483647";
      }
      return reason;
    },
    "EMERGENCY-FLAG-INCOMING": function isInputInValid() {
      if (!value || !["true", "false"].includes(value.toLowerCase())) {
        reason =
          "emergency-flag-incoming is a required field and should be either true or false";
      }
      return reason;
    },
    "EMERGENCY-FLAG-OUTGOING": function isInputInValid() {
      if (!value || !["true", "false"].includes(value.toLowerCase())) {
        reason =
          "emergency-flag-outgoing is a required field and should be either true or false";
      }
      return reason;
    },
    "PRIORITY-OUTGOING-VALUE": function isInputInValid() {
      if (
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) < Number.MIN_SAFE_INTEGER ||
        parseInt(value, 10) > 2147483647
      ) {
        reason =
          "priority-outgoing-value is a required field and should be less than 2147483647";
      }
      return reason;
    },
  };
  return org[field]();
};

export const handleOrgTranslationFileValidation = (
  file: IOrgTranslationBulkEntryType[]
) => {
  finalFile = [];
  filewitherror = [];

  file.forEach((entry: IOrgTranslationBulkEntryType) => {
    if (Object.values(entry).join("").length > 0) {
      const result = Object.keys(entry).map((field: string) =>
        verifyOrgTranslationEntries(
          field,
          entry[field as keyof IOrgTranslationBulkEntryType]
        )
      );

      const errors = result.filter((err) => err.length > 1);
      // check errors
      if (errors.length > 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]} (+ ${
            errors.length - 1
          } other field(s) with errors)`,
        };
        filewitherror.push(newObj);
      } else if (errors.length === 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]}`,
        };
        filewitherror.push(newObj);
      } else {
        finalFile.push(entry);
      }
    }
  });
  return { finalFile, filewitherror };
};

// Location
interface ILocationBulkEntryType {
  "IWF-ID": string;
  "IWF-LOCATION-NAME": string;
  "IWF-DOMAIN-NAME": string;
}

const verifyLocationEntries = (field: string, value: string) => {
  let reason = "";
  const location: { [key: string]: () => string } = {
    "IWF-ID": function isInputInValid() {
      if (value.length === 0) {
        reason = "iwf-id is a required field";
      }
      return reason;
    },
    "IWF-LOCATION-NAME": function isInputInValid() {
      if (!value || value.length === 0) {
        reason = "iwf-location-name is a required field";
      }
      return reason;
    },
    "IWF-DOMAIN-NAME": function isInputInValid() {
      if (!value || value.length === 0) {
        reason = "iwf-domain-name is a required field";
      }
      return reason;
    },
  };
  return location[field]();
};

export const handleLocationFileValidation = (
  file: ILocationBulkEntryType[]
) => {
  finalFile = [];
  filewitherror = [];
  file.forEach((entry: ILocationBulkEntryType) => {
    if (Object.values(entry).join("").length > 0) {
      const result = Object.keys(entry).map((field: string) =>
        verifyLocationEntries(
          field,
          entry[field as keyof ILocationBulkEntryType]
        )
      );

      const errors = result.filter((err) => err.length > 1);
      // check errors
      if (errors.length > 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]} (+ ${
            errors.length - 1
          } other field(s) with errors)`,
        };
        filewitherror.push(newObj);
      } else if (errors.length === 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]}`,
        };
        filewitherror.push(newObj);
      } else {
        finalFile.push(entry);
      }
    }
  });
  return { finalFile, filewitherror };
};

// Canned Message
interface ICannedMessageBulkEntryType {
  "MESSAGE-ID": string;
  "CANNED-TEXT": string;
}

const verifyCannedMessageEntries = (field: string, value: string) => {
  let reason = "";
  const location: { [key: string]: () => string } = {
    "MESSAGE-ID": function isInputInValid() {
      if (
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) < 0 ||
        parseInt(value, 10) > 65535
      ) {
        reason =
          "message-id is a required field and should be in the range (0-65535)";
      }
      return reason;
    },
    "CANNED-TEXT": function isInputInValid() {
      if (!value || value.length === 0) {
        reason = "canned-text is a required field";
      }
      return reason;
    },
  };
  return location[field]();
};

export const handleCannedMessageFileValidation = (
  file: ICannedMessageBulkEntryType[]
) => {
  finalFile = [];
  filewitherror = [];

  file.forEach((entry: ICannedMessageBulkEntryType) => {
    if (Object.values(entry).join("").length > 0) {
      const result = Object.keys(entry).map((field: string) =>
        verifyCannedMessageEntries(
          field,
          entry[field as keyof ICannedMessageBulkEntryType]
        )
      );

      const errors = result.filter((err) => err.length > 1);
      // check errors
      if (errors.length > 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]} (+ ${
            errors.length - 1
          } other field(s) with errors)`,
        };
        filewitherror.push(newObj);
      } else if (errors.length === 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]}`,
        };
        filewitherror.push(newObj);
      } else {
        finalFile.push(entry);
      }
    }
  });
  return { finalFile, filewitherror };
};

// LMR User
interface ILMRUserEntryType {
  "LMR-ID": string;
  "LMR-NW-TYPE": string;
  "MAPPED-MCPTT-ID": string;
  "DISPLAY-NAME": string;
  "MUTUAL-AID": string;
}

const verifyLMRUserEntries = (field: string, value: string) => {
  const lmruser: { [key: string]: () => string } = {
    "LMR-ID": function isValueInvalid() {
      let reason = "";
      if (!value || value.length === 0) {
        reason = "lmr-id is a required field";
      }
      return reason;
    },
    "LMR-NW-TYPE": function isValueInvalid() {
      let reason = "";
      if (!value || value.length === 0) {
        reason = "lmr-nw-type is a required field";
      }
      return reason;
    },
    "MAPPED-MCPTT-ID": function isValueInvalid() {
      let reason = "";
      if (!value || value.length === 0) {
        reason = "mapped-mcptt-id is a required field";
      }
      return reason;
    },
    "DISPLAY-NAME": function isValueInvalid() {
      let reason = "";
      if (!value || value.length === 0) {
        reason = "display-name is a required field";
      }
      return reason;
    },
    "MUTUAL-AID": function isValueInvalid() {
      let reason = "";
      if (!value || !["true", "false"].includes(value.toLowerCase())) {
        reason = "mutual-aid should be either true of false";
      }
      return reason;
    },
  };

  return lmruser[field]();
};

export const handleLMRUsersFileValidation = (file: ILMRUserEntryType[]) => {
  finalFile = [];
  filewitherror = [];

  file.forEach((entry: ILMRUserEntryType) => {
    if (Object.values(entry).join("").length > 0) {
      const result = Object.keys(entry).map((field: string) =>
        verifyLMRUserEntries(field, entry[field as keyof ILMRUserEntryType])
      );

      const errors = result.filter((err) => err.length > 1);
      // check errors
      if (errors.length > 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]} (+ ${
            errors.length - 1
          } other field(s) with errors)`,
        };
        filewitherror.push(newObj);
      } else if (errors.length === 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]}`,
        };
        filewitherror.push(newObj);
      } else {
        finalFile.push(entry);
      }
    }
  });

  return { finalFile, filewitherror };
};

// LMR Group
interface ILMRGroupEntryType {
  "TG-ID": string;
  "LMR-NW-TYPE": "P25-ISSI" | "P25-DFSI";
  "MAPPED-MCPTT-GID": string;
}

const verifyLMRGroupEntries = (field: string, value: string) => {
  const lmrgroup: { [key: string]: () => string } = {
    "TG-ID": function isValueInvalid() {
      let reason = "";
      if (!value || value.length === 0) {
        reason = "tg-id is a required field";
      }
      return reason;
    },
    "LMR-NW-TYPE": function isValueInvalid() {
      let reason = "";
      if (
        !value ||
        value.length === 0 ||
        !["P25-ISSI", "P25-DFSI"].includes(value)
      ) {
        reason =
          "lmr-nw-type is a required field and should be one of ('P25-ISSI', 'P25-DFSI')";
      }
      return reason;
    },
    "MAPPED-MCPTT-GID": function isValueInvalid() {
      let reason = "";
      if (!value || value.length === 0) {
        reason = "mapped-mcptt-gid is a required field";
      }
      return reason;
    },
  };

  return lmrgroup[field]();
};

export const handleLMRGroupFileValidation = (file: ILMRGroupEntryType[]) => {
  finalFile = [];
  filewitherror = [];

  file.forEach((entry: ILMRGroupEntryType) => {
    if (Object.values(entry).join("").length > 0) {
      const result = Object.keys(entry).map((field: string) =>
        verifyLMRGroupEntries(field, entry[field as keyof ILMRGroupEntryType])
      );
      const errors = result.filter((err) => err.length > 1);
      // check errors
      if (errors.length > 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]} (+ ${
            errors.length - 1
          } other field(s) with errors)`,
        };
        filewitherror.push(newObj);
      } else if (errors.length === 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]}`,
        };
        filewitherror.push(newObj);
      } else {
        finalFile.push(entry);
      }
    }
  });
  return { finalFile, filewitherror };
};

// MCPTT User
interface IMCPTTUserEntryType {
  "MCPTT-ID": string;
  "DISPLAY-NAME": string;
  "MAPPED-P25-ID": string;
  "MAPPED-DFSI-ID": string;
  "MAPPED-ROIP-ID": string;
  "MAPPED-DMR-ID": string;
  "MUTUAL-AID": string;
}

const verifyMCPTTUserEntries = (
  field: string,
  value: string | ({ field: string; value: string } | undefined)[],
  flag: string
) => {
  const mcpttuser: { [key: string]: () => string } = {
    "MCPTT-ID": function isValueInvalid() {
      let reason = "";
      if (!value || value.length === 0) {
        reason = "mcptt-id is a required field";
      }
      return reason;
    },
    "DISPLAY-NAME": function isValueInvalid() {
      let reason = "";
      if (!value || value.length === 0) {
        reason = "display-name is a required field";
      }
      return reason;
    },
    "MUTUAL-AID": function isValueInvalid() {
      let reason = "";
      if (!flag || !["true", "false"].includes(flag.toLowerCase())) {
        reason = "mutual-aid should be either true of false";
      }
      return reason;
    },
    selectedMappedFields: function isValueInvalid() {
      let reason = "";
      // to check null mapped value
      if (!value || value.length === 0) {
        reason = "not allowed to create user when no mapped data is provided";
      }
      // incorrect combination of mapped fields
      else if (value.length > 2) {
        reason =
          "not allowed set more than one mapped value except in the case of P25 and DFSI";
      }
      // incorrect combination of mapped fields
      else if (value.length === 2) {
        const isCombinationValid =
          (typeof value !== "string" &&
            value[0]?.field === "MAPPED-P25-ID" &&
            value[1]?.field === "MAPPED-DFSI-ID") ||
          (typeof value !== "string" &&
            value[1]?.field === "MAPPED-P25-ID" &&
            value[0]?.field === "MAPPED-DFSI-ID");

        if (!isCombinationValid) {
          reason =
            "not allowed set more than one mapped value except in the case of P25 and DFSI";
        }
      }
      // to check null/emprty value for selected mapped field
      else if (value[0] !== undefined && value.length === 1) {
        const isValueValid =
          typeof value !== "string" &&
          [
            "MAPPED-P25-ID",
            "MAPPED-DFSI-ID",
            "MAPPED-ROIP-ID",
            "MAPPED-DMR-ID",
          ].includes(value[0].field) &&
          [value[0].value].length > 0;

        if (!isValueValid) {
          reason =
            "not allowed set more than one mapped value except in the case of P25 and DFSI";
        }
      }
      return reason;
    },
  };
  return mcpttuser[field]();
};

export const handleMCPTTUsersFileValidation = (file: IMCPTTUserEntryType[]) => {
  finalFile = [];
  filewitherror = [];

  file.forEach((entry: IMCPTTUserEntryType) => {
    if (Object.values(entry).join("").length > 0) {
      const selectedMappedFields = [
        "MAPPED-P25-ID",
        "MAPPED-DFSI-ID",
        "MAPPED-ROIP-ID",
        "MAPPED-DMR-ID",
      ]
        .filter(
          (field) => entry[field as keyof IMCPTTUserEntryType]?.length > 0
        )
        .map((field) => {
          let obj;
          if (entry[field as keyof IMCPTTUserEntryType]) {
            obj = {
              field,
              value: entry[field as keyof IMCPTTUserEntryType],
            };
          }
          return obj;
        });
      const newEntry: { [key: string]: any } = {
        "MCPTT-ID": entry["MCPTT-ID"],
        "DISPLAY-NAME": entry["DISPLAY-NAME"],
        "MUTUAL-AID": entry["MUTUAL-AID"],
        selectedMappedFields,
      };
      const result = Object.keys(newEntry).map((field: string) =>
        verifyMCPTTUserEntries(field, newEntry[field], newEntry["MUTUAL-AID"])
      );
      const errors = result.filter((err) => err.length > 1);
      // check errors
      if (errors.length > 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]} (+ ${
            errors.length - 1
          } other field(s) with errors)`,
        };
        filewitherror.push(newObj);
      } else if (errors.length === 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]}`,
        };
        filewitherror.push(newObj);
      } else {
        finalFile.push(entry);
      }
    }
  });

  return { finalFile, filewitherror };
};

// MCPTT Group
interface IMCPTTGroupEntryType {
  "MCPTT-GID": string;
  "MAPPED-P25-TG-ID": string;
  "MAPPED-DFSI-TG-ID": string;
  "MAPPED-ROIP-TG-ID": string;
  "MAPPED-DMR-TG-ID": string;
}

const verifyMCPTTGroupEntries = (
  field: string,
  value: string | ({ field: string; value: string } | undefined)[]
) => {
  const mcpttgroup: { [key: string]: () => string } = {
    "MCPTT-GID": function isValueInvalid() {
      let reason = "";
      if (!value || value.length === 0) {
        reason = "mcptt-gid is a required field";
      }
      return reason;
    },
    selectedMappedFields: function isValueInvalid() {
      let reason = "";
      // to check null mapped values
      if (!value || value.length === 0) {
        reason = "not allowed to create user when no mapped data is provided";
      }
      // incorrect combination of mapped fields
      else if (value.length > 1) {
        reason = "not allowed set more than one mapped value";
      }
      // to check null/emprty value for selected mapped field
      else if (value[0] !== undefined && value.length === 1) {
        const isValueValid =
          typeof value !== "string" &&
          [
            "MAPPED-P25-TG-ID",
            "MAPPED-DFSI-TG-ID",
            "MAPPED-ROIP-TG-ID",
            "MAPPED-DMR-TG-ID",
          ].includes(value[0].field) &&
          [value[0].value].length > 0;

        if (!isValueValid) {
          reason = `${
            typeof value !== "string" && value[0].field
          } is a required field.`;
        }
      }
      return reason;
    },
  };
  return mcpttgroup[field]();
};

export const handleMCPTTGroupsFileValidation = (
  file: IMCPTTGroupEntryType[]
) => {
  finalFile = [];
  filewitherror = [];

  file.forEach((entry: IMCPTTGroupEntryType) => {
    if (Object.values(entry).join("").length > 0) {
      const selectedMappedFields = [
        "MAPPED-P25-TG-ID",
        "MAPPED-DFSI-TG-ID",
        "MAPPED-ROIP-TG-ID",
        "MAPPED-DMR-TG-ID",
      ]
        .filter(
          (field) => entry[field as keyof IMCPTTGroupEntryType]?.length > 0
        )
        .map((field) => {
          let obj;
          if (entry[field as keyof IMCPTTGroupEntryType]) {
            obj = {
              field,
              value: entry[field as keyof IMCPTTGroupEntryType],
            };
          }
          return obj;
        });
      const newEntry: { [key: string]: any } = {
        "MCPTT-GID": entry["MCPTT-GID"],
        selectedMappedFields,
      };
      const result = Object.keys(newEntry).map((field: string) =>
        verifyMCPTTGroupEntries(field, newEntry[field])
      );
      const errors = result.filter((err) => err.length > 1);
      // check errors
      if (errors.length > 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]} (+ ${
            errors.length - 1
          } other field(s) with errors)`,
        };
        filewitherror.push(newObj);
      } else if (errors.length === 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]}`,
        };
        filewitherror.push(newObj);
      } else {
        finalFile.push(entry);
      }
    }
  });

  return { finalFile, filewitherror };
};

// DFSI Connection
const isIPv4AddressInvalid = (val: string) => {
  if (!val || val.length === 0) return true;
  if (
    !/^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
      val
    )
  ) {
    return true;
  }
  return false;
};
interface IDFSIConnectionEntryType {
  "CONNECTION-ID": string;
  "IWF-ROLE": "Host" | "Station";
  "DFSI-VERSION": "1" | "2";
  "HOST-CONTROL-IPV4-ADDR": string;
  "HOST-CONTROL-PORT": number;
  "HOST-VOICE-IPV4-ADDR": string;
  "VOICE-PORT": number;
  "DATA-IPV4-ADDR": string;
  "DATA-PORT": number;
  "FIXED-STATION-CONTROL-IPV4-ADDR": string;
  "FIXED-STATION-VOICE-IPV4-ADDR": string;
  "FIXED-STATION-CONTROL-PORT": number;
  "CONNECTIVITY-TX-TIMER": number;
  "CONNECTIVITY-LOSS-LIMIT": number;
  "FS-HEARTBEAT-PERIOD": number;
  "HOST-HEARTBEAT-PERIOD": number;
  "CONTROL-RETRY-TIME": number;
  "CONTROL-ATTEMPT-LIMIT": number;
  "OUTBOUND-MAX-CAI-DATA-BLOCKS": number;
  "INBOUND-MAX-CAI-DATA-BLOCKS": number;
  "ACKNOWLEDGEMENT-DELAY": number;
  "INBOUND-REPEAT": boolean;
  "FIXED-STATION-VOTER-REPORTING-PERIOD": number;
  "FIXED-STATION-TRANSMITTER-REPORT-INTERVAL": number;
}

const verifyDFSIConnectionEntries = (
  role: string,
  version: number,
  field: string,
  value: any
) => {
  const dfsiconnection: { [key: string]: () => string } = {
    "CONNECTION-ID": function isValueInvalid() {
      let reason = "";
      if (!value || value?.length === 0) {
        reason = "connection-id is a required field";
      }
      return reason;
    },
    "IWF-ROLE": function isValueInvalid() {
      let reason = "";
      if (!value || value?.length === 0) {
        reason = "iwf-role is a required field";
      }
      if (value && !["Host", "Station"].includes(value)) {
        reason = `'${value}' is not one of ['Station', 'Host']`;
      }
      return reason;
    },
    "DFSI-VERSION": function isValueInvalid() {
      let reason = "";
      if (!value || Number.isNaN(parseInt(value, 10))) {
        reason = "dfsi-version is a required field";
      }
      if (
        value &&
        !Number.isNaN(parseInt(value, 10)) &&
        ![1, 2].includes(parseInt(value, 10))
      ) {
        reason = `'${value}' is not one of ['1', '2']`;
      }
      return reason;
    },
    "HOST-CONTROL-IPV4-ADDR": function isValueInvalid() {
      let reason = "";
      const result = isIPv4AddressInvalid(value);
      if (role === "Host") {
        if (result) {
          reason =
            "host-control-ipv4-address is a required field and should be valid ipv4 address";
        }
      } else if (value) {
        reason = "host-control-ipv4-address is only allowed for Host role";
      }
      return reason;
    },
    "HOST-CONTROL-PORT": function isValueInvalid() {
      let reason = "";
      if (role === "Host") {
        if (
          !value ||
          Number.isNaN(parseInt(value, 10)) ||
          parseInt(value, 10) > 65535 ||
          parseInt(value, 10) < 1024
        ) {
          reason =
            "host-control-port is a required field and should be in the range (1024-65535)";
        }
      } else if (Number(value)) {
        reason = "host-control-port is only allowed for Host role";
      }
      return reason;
    },
    "HOST-VOICE-IPV4-ADDR": function isValueInvalid() {
      let reason = "";
      const result = isIPv4AddressInvalid(value);
      if (role === "Host") {
        if (result) {
          reason =
            "host-voice-ipv4-address is a required field and should be valid ipv4 address";
        }
      } else if (value) {
        reason = "host-voice-ipv4-address is only allowed for Host role";
      }
      return reason;
    },
    "VOICE-PORT": function isValueInvalid() {
      let reason = "";
      if (
        !value ||
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) > 65535 ||
        parseInt(value, 10) < 1024
      ) {
        reason =
          "voice-port is a required field and should be in the range (1024-65535)";
      }
      return reason;
    },
    "DATA-IPV4-ADDR": function isValueInvalid() {
      let reason = "";
      const result = isIPv4AddressInvalid(value);
      if (version === 2) {
        if (result) {
          reason =
            "data-voice-ipv4-address is a required field and should be valid ipv4 address";
        }
      } else if (value) {
        reason =
          "data-voice-ipv4-address is only allowed when selected version is 2";
      }
      return reason;
    },
    "DATA-PORT": function isValueInvalid() {
      let reason = "";
      if (version === 2) {
        if (
          !value ||
          Number.isNaN(parseInt(value, 10)) ||
          parseInt(value, 10) > 65535 ||
          parseInt(value, 10) < 1024
        ) {
          reason =
            "data-port is a required field and should be in the range (1024-65535)";
        }
      } else if (value) {
        reason = "data-port is not allowed when version is 1";
      }
      return reason;
    },
    "FIXED-STATION-CONTROL-IPV4-ADDR": function isValueInvalid() {
      let reason = "";
      const result = isIPv4AddressInvalid(value);
      if (result) {
        reason =
          "fixed-station-control-ipv4-address is a required field and should be valid ipv4 address";
      }
      return reason;
    },
    "FIXED-STATION-VOICE-IPV4-ADDR": function isValueInvalid() {
      let reason = "";
      const result = isIPv4AddressInvalid(value);
      if (version === 1 || (version === 2 && role === "Station")) {
        if (result) {
          reason =
            "fixed-station-voice-ipv4-address is a required field and should be valid ipv4 address";
        }
      } else if (value) {
        reason =
          "fixed-station-voice-ipv4-address is not allowed for Host role and version 2";
      }
      return reason;
    },
    "FIXED-STATION-CONTROL-PORT": function isValueInvalid() {
      let reason = "";
      if (
        !value ||
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) > 65535 ||
        parseInt(value, 10) < 1024
      ) {
        reason =
          "fixed-station-control-port is a required field and should be in the range (1024-65535)";
      }
      return reason;
    },
    "CONNECTIVITY-TX-TIMER": function isValueInvalid() {
      let reason = "";
      if (version === 2 && role === "Host") {
        if (
          !value ||
          Number.isNaN(parseInt(value, 10)) ||
          parseInt(value, 10) > 255 ||
          parseInt(value, 10) < 1
        ) {
          reason =
            "connectivity-tx-timer is a required field and should be in the range (1-255)";
        }
      } else if (value) {
        reason =
          "connectivity-tx-timer is only allowed when version is 2 and role is Host";
      }

      return reason;
    },
    "CONNECTIVITY-LOSS-LIMIT": function isValueInvalid() {
      let reason = "";
      if (
        !value ||
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) > 100 ||
        parseInt(value, 10) < 1
      ) {
        reason =
          "connectivity-loss-limit is a required field and should be in the range (1-100)";
      }
      return reason;
    },
    "FS-HEARTBEAT-PERIOD": function isValueInvalid() {
      let reason = "";
      if (version === 1 && role === "Host") {
        if (
          !value ||
          Number.isNaN(parseInt(value, 10)) ||
          parseInt(value, 10) > 255 ||
          parseInt(value, 10) < 1
        ) {
          reason =
            "fs-heartbeat-period is a required field and should be in the range (1-255)";
        }
      } else if (value) {
        reason =
          "fs-heartbeat-period is only allowed when version is 1 and role is Host";
      }
      return reason;
    },
    "HOST-HEARTBEAT-PERIOD": function isValueInvalid() {
      let reason = "";
      if (version === 1 && role === "Host") {
        if (
          !value ||
          Number.isNaN(parseInt(value, 10)) ||
          parseInt(value, 10) > 255 ||
          parseInt(value, 10) < 1
        ) {
          reason =
            "host-heartbeat-period is a required field and should be in the range (1-255)";
        }
      } else if (value) {
        reason =
          "host-heartbeat-period is only allowed when version is 1 and role is Host";
      }

      return reason;
    },
    "CONTROL-RETRY-TIME": function isValueInvalid() {
      let reason = "";
      if (
        !value ||
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) > 5000 ||
        parseInt(value, 10) < 1
      ) {
        reason =
          "control-retry-time is a required field and should be in the range (1-5000)";
      }
      return reason;
    },
    "CONTROL-ATTEMPT-LIMIT": function isValueInvalid() {
      let reason = "";
      if (
        !value ||
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) > 10 ||
        parseInt(value, 10) < 1
      ) {
        reason =
          "control-attempt-limit is a required field and should be in the range (1-10)";
      }
      return reason;
    },
    "OUTBOUND-MAX-CAI-DATA-BLOCKS": function isValueInvalid() {
      let reason = "";
      if (version === 2) {
        if (
          !value ||
          Number.isNaN(parseInt(value, 10)) ||
          parseInt(value, 10) > 128 ||
          parseInt(value, 10) < 1
        ) {
          reason =
            "outbound-max-cai-data-blocks is a required field and should be in the range (1-128)";
        }
      } else if (value) {
        reason = "outbound-max-cai-data-blocks is only allowed for version 2";
      }
      return reason;
    },
    "INBOUND-MAX-CAI-DATA-BLOCKS": function isValueInvalid() {
      let reason = "";
      if (version === 2) {
        if (
          !value ||
          Number.isNaN(parseInt(value, 10)) ||
          parseInt(value, 10) > 128 ||
          parseInt(value, 10) < 1
        ) {
          reason =
            "inbound-max-cai-data-blocks is a required field and should be in the range (1-128)";
        }
      } else if (value) {
        reason = "inbound-max-cai-data-blocks is only allowed for version 2";
      }
      return reason;
    },
    "ACKNOWLEDGEMENT-DELAY": function isValueInvalid() {
      let reason = "";
      if (
        !value ||
        Number.isNaN(parseInt(value, 10)) ||
        parseInt(value, 10) > 2 ||
        parseInt(value, 10) < 0
      ) {
        reason =
          "acknowledgement-delay is a required field and should be in the range (0-2)";
      }
      return reason;
    },
    "INBOUND-REPEAT": function isValueInvalid() {
      let reason = "";
      if (!value || !["true", "false"].includes(value.toLowerCase())) {
        reason =
          "inbound-repeat is a required field and should be either true of false";
      }
      return reason;
    },
    "FIXED-STATION-VOTER-REPORTING-PERIOD": function isValueInvalid() {
      let reason = "";
      if (role === "Station") {
        if (
          !value ||
          Number.isNaN(parseInt(value, 10)) ||
          parseInt(value, 10) > 255 ||
          parseInt(value, 10) < 1
        ) {
          reason =
            "fixed-station-voter-reporting-period is a required field and should be in the range (1-255)";
        }
      } else if (value) {
        reason =
          "fixed-station-voter-reporting-period is only allowed for Station role";
      }

      return reason;
    },
    "FIXED-STATION-TRANSMITTER-REPORT-INTERVAL": function isValueInvalid() {
      let reason = "";
      if (version === 2 && role === "Station") {
        if (
          !value ||
          Number.isNaN(parseInt(value, 10)) ||
          parseInt(value, 10) > 255 ||
          parseInt(value, 10) < 1
        ) {
          reason =
            "fixed-station-transmitter-report-interval is a required field and should be in the range (1-255)";
        }
      } else if (value) {
        reason =
          "fixed-station-transmitter-report-interval is only allowed for Station role and version 2";
      }
      return reason;
    },
  };

  return dfsiconnection[field]();
};

const validateUniqueCombos = ({
  allRecords,
  currentRecord,
  fieldCombos,
}: {
  allRecords: Array<Record<string, any>>;
  currentRecord: Record<string, any>;
  fieldCombos?: string[][];
}): string => {
  const failedCombo = fieldCombos?.find((combo) => {
    return allRecords.some((record) => {
      return combo.every((field) => {
        return record[field]?.toString() === currentRecord[field];
      });
    });
  });
  if (failedCombo) {
    return `The combination of ${fieldNameMapping[failedCombo[0]]} and ${
      fieldNameMapping[failedCombo[1]]
    } is not unique`;
  }
  return "";
};

export const handleDFSIConnectionsFileValidation = (
  file: IDFSIConnectionEntryType[],
  data: []
) => {
  finalFile = [];
  filewitherror = [];
  file.forEach((entry: IDFSIConnectionEntryType) => {
    if (Object.values(entry).join("").length > 0) {
      const result = Object.keys(entry).map((field: string) =>
        verifyDFSIConnectionEntries(
          entry["IWF-ROLE"],
          Number.isNaN(parseInt(entry["DFSI-VERSION"], 10))
            ? 0
            : parseInt(entry["DFSI-VERSION"], 10),
          field,
          entry[field as keyof IDFSIConnectionEntryType]
        )
      );
      const uniqueResult = validateUniqueCombos({
        allRecords: data,
        currentRecord: entry,
        fieldCombos:
          entry["DFSI-VERSION"] && entry["IWF-ROLE"]
            ? getUniqueCombination(
                parseInt(entry["DFSI-VERSION"], 10),
                entry["IWF-ROLE"]
              )
            : [[]],
      });
      if (uniqueResult) {
        result.push(uniqueResult);
      }
      const errors = result.filter((err) => err.length > 1);
      // check errors
      if (errors.length > 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]} (+ ${
            errors.length - 1
          } other field(s) with errors)`,
        };
        filewitherror.push(newObj);
      } else if (errors.length === 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]}`,
        };
        filewitherror.push(newObj);
      } else {
        finalFile.push(entry);
      }
    }
  });
  return { finalFile, filewitherror };
};

// DFSI Channel
interface IDFSIChannelEntryType {
  "CONNECTION-ID": string;
  "CHANNEL-ID": string;
  "DFSI-VERSION": string;
  "OUTBOUND-NETWORK-ACCESS-CODE": 659;
  "INBOUND-NETWORK-ACCESS-CODE": 659;
  "OTA-GROUP-ID": string;
  "CHANNEL-MODE": "Analog" | "Digital";
  "CARRIER-SQUELCH": string;
  "CTC-SQUELCH-SYSTEM": string;
  "CDC-SQUELCH-SYSTEM": string;
}

const verifyDFSIChannelEntries = (
  entry: any,
  field: string,
  dfsiData: { "CONNECTION-ID": string; "DFSI-VERSION": undefined | number }[],
  isInvalidVersions: {
    "CONNECTION-ID": string;
    "DFSI-VERSION": undefined | number;
  }[],
  isChecked?: boolean
) => {
  const dfsichannel: { [key: string]: () => string } = {
    "CONNECTION-ID": function isValueInvalid() {
      let reason = "";
      if (!entry["CONNECTION-ID"] || entry["CONNECTION-ID"]?.length === 0) {
        reason = "connection-id is a required field";
      } else if (!dfsiData[0] && !isInvalidVersions[0]) {
        reason = "no DFSI Connection found for the given connection-id";
      }
      return reason;
    },
    "DFSI-VERSION": function isValueInvalid() {
      let reason = "";
      if (!entry["DFSI-VERSION"]) {
        reason = "dfsi-version is a required field";
      } else if (!dfsiData[0] && isInvalidVersions[0]) {
        reason = "incorrect DFSI Version for the given connection-id";
      }
      return reason;
    },
    "CHANNEL-ID": function isValueInvalid() {
      let reason = "";
      if (!entry["CHANNEL-ID"] || entry["CHANNEL-ID"]?.length === 0) {
        reason = "channel-id is a required field";
      }
      return reason;
    },
    "CHANNEL-MODE": function isisValueInvalid() {
      let reason = "";
      if (
        !entry["CHANNEL-MODE"] ||
        !["Analog", "Digital"].includes(entry["CHANNEL-MODE"])
      ) {
        reason = "channel mode is a required field";
      }
      return reason;
    },
    "CARRIER-SQUELCH": function isValueInvalid() {
      let reason = "";
      if (
        entry["CHANNEL-MODE"] === "Analog" &&
        dfsiData[0]?.["DFSI-VERSION"] === 2
      ) {
        if (
          !entry["CARRIER-SQUELCH"] ||
          !["TRUE", "FALSE"].includes(entry["CARRIER-SQUELCH"])
        ) {
          reason =
            "carrier-squelch is a required field and should be either true or false";
        }
      }
      return reason;
    },
    "OTA-GROUP-ID": function isValueInvalid() {
      let reason = "";
      const value = entry["OTA-GROUP-ID"];
      if (entry["CHANNEL-MODE"] === "Digital") {
        if (
          !value ||
          Number.isNaN(parseInt(value, 10)) ||
          parseInt(value, 10) < 1 ||
          parseInt(value, 10) > 65535
        ) {
          reason =
            "OTA-GROUP-ID is a required field and should be in the range (1, 65535)";
        }
      } else if (value) {
        reason = "OTA-GROUP-ID is only allowed for Digital mode";
      }
      return reason;
    },
    "OUTBOUND-NETWORK-ACCESS-CODE": function isValueInvalid() {
      let reason = "";
      const value = entry["OUTBOUND-NETWORK-ACCESS-CODE"];
      if (entry["CHANNEL-MODE"] === "Digital") {
        if (
          !value ||
          Number.isNaN(parseInt(value, 10)) ||
          Number(value) === 3966 ||
          Number(value) === 3967 ||
          Number(value) < 0 ||
          Number(value) > 4095
        ) {
          reason =
            "OUTBOUND-NETWORK-ACCESS-CODE is a required property and should be in the range (0-4095) except 0xF7E (3966) & 0xF7F (3967)";
        }
      } else if (value) {
        reason =
          "OUTBOUND-NETWORK-ACCESS-CODE is only allowed for Digital mode";
      }
      return reason;
    },
    "INBOUND-NETWORK-ACCESS-CODE": function isValueInvalid() {
      let reason = "";
      const value = entry["INBOUND-NETWORK-ACCESS-CODE"];
      if (entry["CHANNEL-MODE"] === "Digital") {
        if (
          !value ||
          Number.isNaN(parseInt(value, 10)) ||
          Number(value) === 3966 ||
          Number(value) === 3967 ||
          Number(value) < 0 ||
          Number(value) > 4095
        ) {
          reason =
            "INBOUND-NETWORK-ACCESS-CODE is a required property and should be in the range (0-4095) except 0xF7E (3966) & 0xF7F (3967)";
        }
      } else if (value) {
        reason = "INBOUND-NETWORK-ACCESS-CODE is only allowed for Digital mode";
      }
      return reason;
    },
    "CTC-SQUELCH-SYSTEM": function isValueInvalid() {
      let reason = "";
      const value = entry["CTC-SQUELCH-SYSTEM"];
      if (
        entry["CHANNEL-MODE"] === "Analog" &&
        dfsiData[0]?.["DFSI-VERSION"] === 2
      ) {
        if (
          (isChecked && Number.isNaN(parseInt(value, 10))) ||
          (isChecked && parseInt(value, 10) !== 0)
        ) {
          reason =
            "not allowed to set CTC-SQUELCH-SYSTEM when carrier mode is set to true";
        }
        if (!isChecked && entry["CDC-SQUELCH-SYSTEM"] === "") {
          if (
            value !== "" &&
            !ADD_FORM_FIELDS_TRANSLATIONS[Page.DFSI_CHANNEL][
              "CTC-SQUELCH-SYSTEM"
            ].items.includes(value)
          ) {
            reason =
              "CTC-SQUELCH-SYSTEM is not one of [0,670,693,719,744,770,797,825,854,885,915,948,974,1000,1035,1072,1109,1148,1188,1230,1273,1318,1365,1413,1462,1514,1567,1622,1679,1738,1799,1862,1928,2035,2065,2107,2181,2257,2291,2336,2418,2503,2541]";
          }
        }
      } else if (value && value !== "") {
        reason =
          "CTC-SQUELCH-SYSTEM is only allowed for Analog mode and version 2";
      }
      return reason;
    },
    "CDC-SQUELCH-SYSTEM": function isValueInvalid() {
      let reason = "";
      const value = entry["CDC-SQUELCH-SYSTEM"];
      if (
        entry["CHANNEL-MODE"] === "Analog" &&
        dfsiData[0]?.["DFSI-VERSION"] === 2
      ) {
        if (
          (isChecked && Number.isNaN(parseInt(value, 10))) ||
          (isChecked && parseInt(value, 10) !== 0)
        ) {
          reason =
            "not allowed to set CDC-SQUELCH-SYSTEM when carrier mode is set to true";
        }
        if (!isChecked && entry["CTC-SQUELCH-SYSTEM"] === "") {
          if (
            value !== "" &&
            !ADD_FORM_FIELDS_TRANSLATIONS[Page.DFSI_CHANNEL][
              "CDC-SQUELCH-SYSTEM"
            ].items.includes(value)
          ) {
            reason =
              "CDC-SQUELCH-SYSTEM is not one of [19,21,22,25,26,30,35,39,41,43,44,53,57,58,59,60,76,77,78,82,85,89,90,92,99,101,106,109,110,114,117,122,124,133,138,147,149,150,163,164,165,166,169,170,173,177,179,181,182,185,188,198,201,205,213,217,218,227,230,233,238,244,245,249,265,266,267,275,281,282,293,294,298,300,301,306,308,309,310,323,326,334,339,342,346,358,373,390,394,404,407,409,410,421,428,434,436,451,458,467,473,474,476,483,492]";
          }
        }
      } else if (value && value !== "") {
        reason =
          "CDC-SQUELCH-SYSTEM is only allowed for Analog mode and version 2";
      }
      return reason;
    },
  };

  return dfsichannel[field]();
};

export const handleDFSIChannelsFileValidation = (
  file: IDFSIChannelEntryType[],
  data: { "CONNECTION-ID": string; "DFSI-VERSION": number | undefined }[]
) => {
  finalFile = [];
  filewitherror = [];
  file.forEach((entry: IDFSIChannelEntryType) => {
    if (Object.values(entry).join("").length > 0) {
      const dfsiData = data.filter(
        (row) =>
          entry["CONNECTION-ID"] === row["CONNECTION-ID"] &&
          Number(entry["DFSI-VERSION"]) === row["DFSI-VERSION"]
      );
      const isInvalidVersions = data.filter(
        (row) =>
          entry["CONNECTION-ID"] === row["CONNECTION-ID"] &&
          Number(entry["DFSI-VERSION"]) !== row["DFSI-VERSION"]
      );

      const result = Object.keys(entry).map((field: string) =>
        verifyDFSIChannelEntries(
          entry,
          field,
          dfsiData,
          isInvalidVersions,
          entry["CARRIER-SQUELCH"].toLowerCase() === "true"
        )
      );

      const errors = result.filter((err) => err.length > 1);
      // check errors
      if (errors.length > 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]} (+ ${
            errors.length - 1
          } other field(s) with errors)`,
        };
        filewitherror.push(newObj);
      } else if (errors.length === 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]}`,
        };
        filewitherror.push(newObj);
      } else {
        finalFile.push(entry);
      }
    }
  });
  return { finalFile, filewitherror };
};

// DFSI Group
interface IDFSIGroupEntryType {
  "ENCRYPTION-ENABLED": string;
  "STORAGE-LOCATION-NUMBER"?: string;
  FORMAT: "FreeForm" | "PreFixed";
  "DFSI-LOGICAL-GID": string;
  "CHANNEL-IDS": string;
  "DFSI-GROUP-NAME": string;
}

const verifyDFSIGroupEntries = (
  entry: any,
  field: string,
  channelIds: string[]
) => {
  const dfsigroup: { [key: string]: () => string } = {
    "ENCRYPTION-ENABLED": function isValueInvalid() {
      let reason = "";
      const isValid = ["true", "false"].includes(
        entry["ENCRYPTION-ENABLED"].toLowerCase()
      );
      if (!isValid) {
        reason =
          "encryption-enabled is a required field and should be either true or false";
      }
      return reason;
    },
    "STORAGE-LOCATION-NUMBER": function isValueInvalid() {
      let reason = "";
      if (entry["ENCRYPTION-ENABLED"].toLowerCase() === "true") {
        if (
          Number.isNaN(parseInt(entry["STORAGE-LOCATION-NUMBER"], 10)) ||
          parseInt(entry["STORAGE-LOCATION-NUMBER"], 10) < 0 ||
          parseInt(entry["STORAGE-LOCATION-NUMBER"], 10) > 65535
        ) {
          reason =
            "storage location number is a required field and should be in the range(0-65535)";
        }
      } else if (
        !Number.isNaN(parseInt(entry["STORAGE-LOCATION-NUMBER"], 10))
      ) {
        reason =
          "not allowed to set storage location number when encryption enabled is not set";
      }
      return reason;
    },
    FORMAT: function isValueInvalid() {
      let reason = "";
      if (!entry.FORMAT || !["PreFixed", "FreeForm"].includes(entry.FORMAT)) {
        reason =
          "format is a required field and should be either PreFixed or FreeForm";
      }
      return reason;
    },
    "DFSI-LOGICAL-GID": function isValueInvalid() {
      let reason = "";
      if (entry.FORMAT === "PreFixed") {
        if (
          !entry["DFSI-LOGICAL-GID"] ||
          Number.isNaN(Number(entry["DFSI-LOGICAL-GID"])) ||
          entry["DFSI-LOGICAL-GID"].substring(2).length !== 4
        ) {
          reason = "DFSI logical group ID should be 4 characters long";
        }
      } else if (entry.FORMAT === "FreeForm" && !entry["DFSI-LOGICAL-GID"]) {
        reason = "DFSI logical group ID is a required field";
      }
      return reason;
    },
    "CHANNEL-IDS": function isValueInvalid() {
      let reason = "";
      const inputChannelIds = entry["CHANNEL-IDS"]
        .split(",")
        .map((id: string) => id.trim());
      if (!entry["CHANNEL-IDS"] || entry["CHANNEL-IDS"].length === 0) {
        reason = "channel id is a required field";
      } else if (entry["CHANNEL-IDS"].split(",").length === 1) {
        if (!channelIds.includes(entry["CHANNEL-IDS"])) {
          reason =
            "channel-id is invalid. A DFSI Channel should exist to create a new DFSI Group. Please create DFSI channel";
        }
      } else if (entry["CHANNEL-IDS"].split(",").length > 1) {
        const isChannelIdsValid =
          channelIds.filter((id) => inputChannelIds.includes(id)).length ===
          inputChannelIds.length;
        if (!isChannelIdsValid) {
          reason =
            "one of the channel-id is invalid. A DFSI Channel should exist to create a new DFSI Group.";
        }
      }
      return reason;
    },
    "DFSI-GROUP-NAME": function isValueInvalid() {
      let reason = "";
      if (!entry["DFSI-GROUP-NAME"]) {
        reason = "DFSI group name is a required field";
      }
      return reason;
    },
  };

  return dfsigroup[field]();
};

export const handleDFSIGroupsFileValidation = (
  file: IDFSIGroupEntryType[],
  channelIDs: string[]
) => {
  finalFile = [];
  filewitherror = [];
  file.forEach((entry: IDFSIGroupEntryType) => {
    if (Object.values(entry).join("").length > 0) {
      // check if any of the field value id invalid
      const result = Object.keys(entry).map((field: string) =>
        verifyDFSIGroupEntries(entry, field, channelIDs)
      );

      const errors = result.filter((err) => err.length > 1);
      // check errors
      if (errors.length > 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]} (+ ${
            errors.length - 1
          } other field(s) with errors)`,
        };
        filewitherror.push(newObj);
      } else if (errors.length === 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]}`,
        };
        filewitherror.push(newObj);
      } else {
        finalFile.push(entry);
      }
    }
  });
  return { finalFile, filewitherror };
};

// XSSI Connection
interface XSSIConnectionEntryType {
  "CONNECTION-ID": string;
  "RADIO-IPV4-ADDR": string;
  "RADIO-RFSS-ID": number;
  "RADIO-SYSTEM-ID": number;
  "RADIO-WACN-ID": number;
  "RADIO-RFSS-4-HEX-DIGIT": string;
  "IWF-RFSS-ID": number;
  "IWF-SYSTEM-ID": number;
  "IWF-WACN-ID": number;
  "RESPONSES-BEFORE-CONNECTED": number;
  "MISSED-RESPONSES-BEFORE-DISCONNECTED": number;
  "VOCODER-MODES": string[];
  "POLLING-ENABLED": string;
  "POLLING-INTERVAL-CONNECTED": number;
  "POLLING-INTERVAL-DISCONNECTED": number;
  "UNIT-MIN-RANGE": string;
  "UNIT-MAX-RANGE": string;
  "IWF-MIN-RANGE": string;
  "IWF-MAX-RANGE": string;
  "RADIO-MIN-RANGE": string;
  "RADIO-MAX-RANGE": string;
}

export const transformXSSIConnectionPayload = (
  payload: XSSIConnectionEntryType[]
) => {
  const transformedXSSICsvPayload = [];
  let iwfRange: {
    "GROUP-MAX-ID": number | undefined;
    "GROUP-MIN-ID": number | undefined;
  }[] = [];
  let unitRange: {
    "UNIT-MAX-ID": number | undefined;
    "UNIT-MIN-ID": number | undefined;
  }[] = [];
  let radioRange: {
    "GROUP-MAX-ID": number | undefined;
    "GROUP-MIN-ID": number | undefined;
  }[] = [];
  let i = 0;
  while (i < payload.length) {
    let transformedXSSIConnectionEntry = {};
    if (payload[i]["IWF-MAX-RANGE"] || payload[i]["IWF-MIN-RANGE"]) {
      iwfRange.push({
        "GROUP-MAX-ID": Number.isNaN(parseInt(payload[i]["IWF-MAX-RANGE"], 10))
          ? undefined
          : Number(payload[i]["IWF-MAX-RANGE"]),
        "GROUP-MIN-ID": Number.isNaN(parseInt(payload[i]["IWF-MIN-RANGE"], 10))
          ? undefined
          : Number(payload[i]["IWF-MIN-RANGE"]),
      });
    }
    if (payload[i]["UNIT-MAX-RANGE"] || payload[i]["UNIT-MIN-RANGE"]) {
      unitRange.push({
        "UNIT-MAX-ID": Number.isNaN(parseInt(payload[i]["UNIT-MAX-RANGE"], 10))
          ? undefined
          : Number(payload[i]["UNIT-MAX-RANGE"]),
        "UNIT-MIN-ID": Number.isNaN(parseInt(payload[i]["UNIT-MIN-RANGE"], 10))
          ? undefined
          : Number(payload[i]["UNIT-MIN-RANGE"]),
      });
    }
    if (payload[i]["RADIO-MAX-RANGE"] || payload[i]["RADIO-MIN-RANGE"]) {
      radioRange.push({
        "GROUP-MAX-ID": Number.isNaN(
          parseInt(payload[i]["RADIO-MAX-RANGE"], 10)
        )
          ? undefined
          : Number(payload[i]["RADIO-MAX-RANGE"]),
        "GROUP-MIN-ID": Number.isNaN(
          parseInt(payload[i]["RADIO-MIN-RANGE"], 10)
        )
          ? undefined
          : Number(payload[i]["RADIO-MIN-RANGE"]),
      });
    }
    let j = i;

    while (
      j + 1 < payload.length &&
      payload[j]["CONNECTION-ID"] &&
      payload[j]["CONNECTION-ID"] === payload[j + 1]["CONNECTION-ID"]
    ) {
      if (payload[j + 1]["IWF-MAX-RANGE"] && payload[j + 1]["IWF-MIN-RANGE"]) {
        iwfRange.push({
          "GROUP-MAX-ID": Number.isNaN(
            parseInt(payload[j + 1]["IWF-MAX-RANGE"], 10)
          )
            ? undefined
            : Number(payload[j + 1]["IWF-MAX-RANGE"]),
          "GROUP-MIN-ID": Number.isNaN(
            parseInt(payload[j + 1]["IWF-MIN-RANGE"], 10)
          )
            ? undefined
            : Number(payload[j + 1]["IWF-MIN-RANGE"]),
        });
      }
      if (
        payload[j + 1]["UNIT-MIN-RANGE"] &&
        payload[j + 1]["UNIT-MAX-RANGE"]
      ) {
        unitRange.push({
          "UNIT-MAX-ID": Number.isNaN(
            parseInt(payload[j + 1]["UNIT-MAX-RANGE"], 10)
          )
            ? undefined
            : Number(payload[j + 1]["UNIT-MAX-RANGE"]),
          "UNIT-MIN-ID": Number.isNaN(
            parseInt(payload[j + 1]["UNIT-MIN-RANGE"], 10)
          )
            ? undefined
            : Number(payload[j + 1]["UNIT-MIN-RANGE"]),
        });
      }
      if (
        payload[j + 1]["RADIO-MIN-RANGE"] &&
        payload[j + 1]["RADIO-MAX-RANGE"]
      ) {
        radioRange.push({
          "GROUP-MAX-ID": Number.isNaN(
            parseInt(payload[j + 1]["RADIO-MAX-RANGE"], 10)
          )
            ? undefined
            : Number(payload[j + 1]["RADIO-MAX-RANGE"]),
          "GROUP-MIN-ID": Number.isNaN(
            parseInt(payload[j + 1]["RADIO-MIN-RANGE"], 10)
          )
            ? undefined
            : Number(payload[j + 1]["RADIO-MIN-RANGE"]),
        });
      }
      j += 1;
    }
    transformedXSSIConnectionEntry = {
      "CONNECTION-ID": payload[j]["CONNECTION-ID"],
      "RADIO-IPV4-ADDR": payload[j]["RADIO-IPV4-ADDR"],
      "RADIO-RFSS-ID": payload[j]["RADIO-RFSS-ID"],
      "RADIO-SYSTEM-ID": payload[j]["RADIO-SYSTEM-ID"],
      "RADIO-WACN-ID": payload[j]["RADIO-WACN-ID"],
      "RADIO-RFSS-4-HEX-DIGIT":
        payload[j]["RADIO-RFSS-4-HEX-DIGIT"] &&
        payload[j]["RADIO-RFSS-4-HEX-DIGIT"].toLowerCase() === "true",
      "IWF-RFSS-ID": payload[j]["IWF-RFSS-ID"],
      "IWF-SYSTEM-ID": payload[j]["IWF-SYSTEM-ID"],
      "IWF-WACN-ID": payload[j]["IWF-WACN-ID"],
      "RESPONSES-BEFORE-CONNECTED": payload[j]["RESPONSES-BEFORE-CONNECTED"],
      "MISSED-RESPONSES-BEFORE-DISCONNECTED":
        payload[j]["MISSED-RESPONSES-BEFORE-DISCONNECTED"],
      "VOCODER-MODES": payload[j]["VOCODER-MODES"],
      "UNIT-RANGES": unitRange,
      "IWF-GROUP-RANGES": iwfRange,
      "RADIO-GROUP-RANGES": radioRange,
      "POLLING-ENABLED":
        payload[j]["POLLING-ENABLED"] &&
        payload[j]["POLLING-ENABLED"].toLowerCase() === "true",
      "POLLING-INTERVAL-CONNECTED": payload[j]["POLLING-INTERVAL-CONNECTED"],
      "POLLING-INTERVAL-DISCONNECTED":
        payload[j]["POLLING-INTERVAL-DISCONNECTED"],
    };
    transformedXSSICsvPayload.push(transformedXSSIConnectionEntry);
    i = j + 1;
    iwfRange = [];
    unitRange = [];
    radioRange = [];
  }
  return transformedXSSICsvPayload;
};

interface tranformedXSSI {
  "CONNECTION-ID": string;
  "RADIO-IPV4-ADDR": string;
  "RADIO-RFSS-ID": number;
  "RADIO-SYSTEM-ID": number;
  "RADIO-WACN-ID": number;
  "RADIO-RFSS-4-HEX-DIGIT": boolean;
  "IWF-RFSS-ID": number;
  "IWF-SYSTEM-ID": number;
  "IWF-WACN-ID": number;
  "RESPONSES-BEFORE-CONNECTED": number;
  "MISSED-RESPONSES-BEFORE-DISCONNECTED": number;
  "VOCODER-MODES": string[];
  "POLLING-ENABLED": boolean;
  "POLLING-INTERVAL-CONNECTED": number;
  "POLLING-INTERVAL-DISCONNECTED": number;
  "UNIT-RANGES": [];
  "IWF-GROUP-RANGES": [];
  "RADIO-GROUP-RANGES": [];
}

const isUnitRangeValid = (unitRange: any[]) => {
  const isUnitMinRangeInvalid =
    unitRange.filter(
      (range) =>
        !range["UNIT-MIN-ID"] ||
        range["UNIT-MIN-ID"] === undefined ||
        range["UNIT-MIN-ID"] < 1 ||
        range["UNIT-MIN-ID"] > 16777215 ||
        range["UNIT-MIN-ID"] > range["UNIT-MAX-ID"]
    ).length > 1;
  const isUnitMaxRangeInvalid =
    unitRange.filter(
      (range) =>
        !range["UNIT-MAX-ID"] ||
        range["UNIT-MAX-ID"] === undefined ||
        range["UNIT-MAX-ID"] > 16777215 ||
        range["UNIT-MAX-ID"] < 1 ||
        range["UNIT-MAX-ID"] < range["UNIT-MIN-ID"]
    ).length > 1;

  return {
    isMinIDInvalid: isUnitMinRangeInvalid,
    isMaxIDInvalid: isUnitMaxRangeInvalid,
    unitMinIDInvalidReadon: isUnitMinRangeInvalid
      ? "Unit Min ID should be in the range [1, 16777215] and less than Unit Max ID"
      : "",
    unitMaxIDInvalidReadon: isUnitMaxRangeInvalid
      ? "Unit Max ID should be in the range [1, 16777215] and greater than Unit Min ID"
      : "",
  };
};

const isGroupRangeValid = (groupRange: any[]) => {
  const isGroupMinRangeInvalid =
    groupRange.filter(
      (range) =>
        !range["GROUP-MIN-ID"] ||
        range["GROUP-MIN-ID"] === undefined ||
        range["GROUP-MIN-ID"] < 1 ||
        range["GROUP-MIN-ID"] > 65535 ||
        range["GROUP-MIN-ID"] > range["GROUP-MAX-ID"]
    ).length > 0;
  const isGroupMaxRangeInvalid =
    groupRange.filter(
      (range) =>
        !range["GROUP-MAX-ID"] ||
        range["GROUP-MAX-ID"] === undefined ||
        range["GROUP-MAX-ID"] > 65535 ||
        range["GROUP-MAX-ID"] < 1 ||
        range["GROUP-MAX-ID"] < range["GROUP-MIN-ID"]
    ).length > 0;

  return {
    isMinIDInvalid: isGroupMinRangeInvalid,
    isMaxIDInvalid: isGroupMaxRangeInvalid,
    groupMinIDInvalidReadon: isGroupMinRangeInvalid
      ? "Group Min ID should be in the range [1, 65535] and less than Group Max ID"
      : "",
    groupMaxIDInvalidReadon: isGroupMaxRangeInvalid
      ? "Group Max ID should be in the range [1, 65535] and greater than Group Min ID"
      : "",
  };
};

export const handleXSSIConnectionFileValidation = (file: tranformedXSSI[]) => {
  finalFile = [];
  filewitherror = [];

  file.forEach((entry: tranformedXSSI) => {
    if (Object.values(entry).join("").length > 0) {
      let reason = "";
      const result = [];
      const isUnitRangeInvalid =
        entry["UNIT-RANGES"]?.length > 0
          ? isUnitRangeValid(entry["UNIT-RANGES"])
          : false;
      const isIWFGroupRangeInvalid =
        entry["IWF-GROUP-RANGES"]?.length > 0
          ? isGroupRangeValid(entry["IWF-GROUP-RANGES"])
          : false;
      const isRadioGroupRangeInvalid =
        entry["RADIO-GROUP-RANGES"]?.length > 0
          ? isGroupRangeValid(entry["RADIO-GROUP-RANGES"])
          : false;
      if (!entry["CONNECTION-ID"] || entry["CONNECTION-ID"].length === 0) {
        reason = "connection-id is a required field";
        result.push(reason);
      }
      if (
        !entry["RADIO-IPV4-ADDR"] ||
        entry["RADIO-IPV4-ADDR"].length === 0 ||
        !/^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
          entry["RADIO-IPV4-ADDR"]
        )
      ) {
        reason =
          "radio-ipv4-address is a required property and should be a valid IPV4";
        result.push(reason);
      }
      if (
        !entry["RADIO-RFSS-ID"] ||
        entry["RADIO-RFSS-ID"] < 1 ||
        entry["RADIO-RFSS-ID"] > 65535
      ) {
        reason =
          "radio-rfss-id is a required property and should be in the range (1-65535)";
        result.push(reason);
      }
      if (
        !entry["RADIO-SYSTEM-ID"] ||
        entry["RADIO-SYSTEM-ID"] < 1 ||
        entry["RADIO-SYSTEM-ID"] > 4095
      ) {
        reason =
          "radio-system-id is a required property and should be in the range (1-4095)";
        result.push(reason);
      }
      if (
        !entry["RADIO-WACN-ID"] ||
        entry["RADIO-WACN-ID"] < 1 ||
        entry["RADIO-WACN-ID"] > 1048575
      ) {
        reason =
          "radio-wacn-id is a required property and should be in the range (1-1048575)";
        result.push(reason);
      }
      if (
        !entry["IWF-RFSS-ID"] ||
        entry["IWF-RFSS-ID"] < 1 ||
        entry["IWF-RFSS-ID"] > 65535
      ) {
        reason =
          "iwf-rfss-id is a required property and should be in the range (1-65535)";
        result.push(reason);
      }
      if (
        !entry["IWF-SYSTEM-ID"] ||
        entry["IWF-SYSTEM-ID"] < 1 ||
        entry["IWF-SYSTEM-ID"] > 4095
      ) {
        reason =
          "iwf-system-id is a required property and should be in the range (1-4095)";
        result.push(reason);
      }
      if (
        !entry["IWF-WACN-ID"] ||
        entry["IWF-WACN-ID"] < 1 ||
        entry["IWF-WACN-ID"] > 1048575
      ) {
        reason =
          "iwf-wacn-id is a required property and should be in the range (1-1048575)";
        result.push(reason);
      }
      if (
        !entry["RESPONSES-BEFORE-CONNECTED"] ||
        entry["RESPONSES-BEFORE-CONNECTED"] < 1 ||
        entry["RESPONSES-BEFORE-CONNECTED"] > 100
      ) {
        reason =
          "responses-before-connected is a required property and should be in the range (1-100)";
        result.push(reason);
      }
      if (
        !entry["MISSED-RESPONSES-BEFORE-DISCONNECTED"] ||
        entry["MISSED-RESPONSES-BEFORE-DISCONNECTED"] < 1 ||
        entry["MISSED-RESPONSES-BEFORE-DISCONNECTED"] > 100
      ) {
        reason =
          "missed-responses-before-disconnected is a required property and should be in the range (1-100)";
        result.push(reason);
      }
      if (
        !entry["VOCODER-MODES"] ||
        !["FR", "HR", "Native", "Phase 1"].some((r) =>
          entry["VOCODER-MODES"].includes(r.trim())
        )
      ) {
        reason =
          "vocoder-modes is a required property and allowed values are ['FR', 'HR', 'Native', 'Phase 1']";
        result.push(reason);
      }
      if (
        !entry["POLLING-INTERVAL-CONNECTED"] ||
        entry["POLLING-INTERVAL-CONNECTED"] < 1 ||
        entry["POLLING-INTERVAL-CONNECTED"] > 300
      ) {
        reason =
          "polling-interval-connected is a required property and should be in the range (1-300)";
        result.push(reason);
      }
      if (
        !entry["POLLING-INTERVAL-DISCONNECTED"] ||
        entry["POLLING-INTERVAL-DISCONNECTED"] < 1 ||
        entry["POLLING-INTERVAL-DISCONNECTED"] > 300
      ) {
        reason =
          "polling-interval-disconnected is a required property and should be in the range (1-300)";
        result.push(reason);
      }
      if (
        (isUnitRangeInvalid && isUnitRangeInvalid.isMinIDInvalid) ||
        (isUnitRangeInvalid && isUnitRangeInvalid.isMaxIDInvalid)
      ) {
        reason =
          isUnitRangeInvalid.unitMinIDInvalidReadon ||
          isUnitRangeInvalid.unitMaxIDInvalidReadon;
        result.push(reason);
      }
      if (
        (isIWFGroupRangeInvalid && isIWFGroupRangeInvalid.isMinIDInvalid) ||
        (isIWFGroupRangeInvalid && isIWFGroupRangeInvalid.isMaxIDInvalid)
      ) {
        reason =
          isIWFGroupRangeInvalid.groupMinIDInvalidReadon ||
          isIWFGroupRangeInvalid.groupMaxIDInvalidReadon;
        result.push(reason);
      }
      if (
        (isRadioGroupRangeInvalid && isRadioGroupRangeInvalid.isMinIDInvalid) ||
        (isRadioGroupRangeInvalid && isRadioGroupRangeInvalid.isMaxIDInvalid)
      ) {
        reason =
          isRadioGroupRangeInvalid.groupMinIDInvalidReadon ||
          isRadioGroupRangeInvalid.groupMaxIDInvalidReadon;
        result.push(reason);
      }

      const errors = result.filter((err) => err.length > 1);

      // check errors
      if (errors.length > 1) {
        // Transfrom XSSI connection entry
        const newObj = {
          ...entry,
        };
        const maxIndex = Math.max(
          newObj["IWF-GROUP-RANGES"].length,
          newObj["RADIO-GROUP-RANGES"].length,
          newObj["UNIT-RANGES"].length,
          1
        );
        let transformedObject: { [key: string]: any } = {};
        Array.from(Array(maxIndex).keys()).forEach((ind) => {
          transformedObject = {
            ...newObj,
            "IWF-MAX-RANGE": newObj["IWF-GROUP-RANGES"][ind]
              ? newObj["IWF-GROUP-RANGES"][ind]["GROUP-MAX-ID"]
              : "",
            "IWF-MIN-RANGE": newObj["IWF-GROUP-RANGES"][ind]
              ? newObj["IWF-GROUP-RANGES"][ind]["GROUP-MIN-ID"]
              : "",
            "RADIO-MAX-RANGE": newObj["RADIO-GROUP-RANGES"][ind]
              ? newObj["RADIO-GROUP-RANGES"][ind]["GROUP-MAX-ID"]
              : "",
            "RADIO-MIN-RANGE": newObj["RADIO-GROUP-RANGES"][ind]
              ? newObj["RADIO-GROUP-RANGES"][ind]["GROUP-MIN-ID"]
              : "",
            "UNIT-MAX-RANGE": newObj["UNIT-RANGES"][ind]
              ? newObj["UNIT-RANGES"][ind]["UNIT-MAX-ID"]
              : "",
            "UNIT-MIN-RANGE": newObj["UNIT-RANGES"][ind]
              ? newObj["UNIT-RANGES"][ind]["UNIT-MIN-ID"]
              : "",
            reason: `${errors[0]} (+ ${
              errors.length - 1
            } other field(s) with errors)`,
          };
          delete transformedObject["IWF-GROUP-RANGES"];
          delete transformedObject["RADIO-GROUP-RANGES"];
          delete transformedObject["UNIT-RANGES"];
          filewitherror.push(transformedObject);
        });
      } else if (errors.length === 1) {
        // Transfrom XSSI connection entry
        const newObj = {
          ...entry,
        };

        const maxIndex = Math.max(
          newObj["IWF-GROUP-RANGES"].length,
          newObj["RADIO-GROUP-RANGES"].length,
          newObj["UNIT-RANGES"].length,
          1
        );
        let transformedObject: { [key: string]: any } = {};
        Array.from(Array(maxIndex).keys()).forEach((ind) => {
          transformedObject = {
            ...newObj,
            "IWF-MAX-RANGE": newObj["IWF-GROUP-RANGES"][ind]
              ? newObj["IWF-GROUP-RANGES"][ind]["GROUP-MAX-ID"]
              : "",
            "IWF-MIN-RANGE": newObj["IWF-GROUP-RANGES"][ind]
              ? newObj["IWF-GROUP-RANGES"][ind]["GROUP-MIN-ID"]
              : "",
            "RADIO-MAX-RANGE": newObj["RADIO-GROUP-RANGES"][ind]
              ? newObj["RADIO-GROUP-RANGES"][ind]["GROUP-MAX-ID"]
              : "",
            "RADIO-MIN-RANGE": newObj["RADIO-GROUP-RANGES"][ind]
              ? newObj["RADIO-GROUP-RANGES"][ind]["GROUP-MIN-ID"]
              : "",
            "UNIT-MAX-RANGE": newObj["UNIT-RANGES"][ind]
              ? newObj["UNIT-RANGES"][ind]["UNIT-MAX-ID"]
              : "",
            "UNIT-MIN-RANGE": newObj["UNIT-RANGES"][ind]
              ? newObj["UNIT-RANGES"][ind]["UNIT-MIN-ID"]
              : "",
            reason: `${errors[0]}`,
          };

          delete transformedObject["IWF-GROUP-RANGES"];
          delete transformedObject["RADIO-GROUP-RANGES"];
          delete transformedObject["UNIT-RANGES"];

          filewitherror.push(transformedObject);
        });
      } else {
        finalFile.push(entry);
      }
    }
  });

  return { finalFile, filewitherror };
};

// Organisation Mapping
interface IOrgMappingBulkEntryType {
  "IWF-ID": string;
  "ORGANISATION-ID": string;
}

const verifyOrgMappingEntries = (field: string, value: string) => {
  let reason = "";
  const orgMapping: { [key: string]: () => string } = {
    "IWF-ID": function isInputInValid() {
      if (!value || value.length === 0) {
        reason = "iwf-id is a required field";
      }
      return reason;
    },
    "ORGANISATION-ID": function isInputInValid() {
      if (!value || value.length === 0) {
        reason = "org-id is a required field";
      }
      return reason;
    },
  };
  return orgMapping[field]();
};

export const handleOrgMappingFileValidation = (
  file: IOrgMappingBulkEntryType[]
) => {
  finalFile = [];
  filewitherror = [];

  file.forEach((entry: IOrgMappingBulkEntryType) => {
    if (Object.values(entry).join("").length > 0) {
      const result = Object.keys(entry).map((field: string) =>
        verifyOrgMappingEntries(
          field,
          entry[field as keyof IOrgMappingBulkEntryType]
        )
      );

      const errors = result.filter((err) => err.length > 1);
      // check errors
      if (errors.length > 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]} (+ ${
            errors.length - 1
          } other field(s) with errors)`,
        };
        filewitherror.push(newObj);
      } else if (errors.length === 1) {
        const newObj = {
          ...entry,
          reason: `${errors[0]}`,
        };
        filewitherror.push(newObj);
      } else {
        finalFile.push(entry);
      }
    }
  });
  return { finalFile, filewitherror };
};

export const handleNewBulkFileValidation = (moduleName: string) => {
  if (moduleName === "Organisation") return handleOrganisationFileValidation;
  if (moduleName === "Organisation Translation")
    return handleOrgTranslationFileValidation;
  if (moduleName === "Organisation Mapping")
    return handleOrgMappingFileValidation;
  if (moduleName === "OTAR Connection")
    return handleOTARConnectionFileValidation;
  if (moduleName === "Location") return handleLocationFileValidation;
  if (moduleName === "Canned Message") return handleCannedMessageFileValidation;
  if (moduleName === "LMR User") return handleLMRUsersFileValidation;
  if (moduleName === "LMR Group") return handleLMRGroupFileValidation;
  if (moduleName === "MCPTT User") return handleMCPTTUsersFileValidation;
  if (moduleName === "MCPTT Group") return handleMCPTTGroupsFileValidation;
  if (moduleName === "DFSI Connection")
    return handleDFSIConnectionsFileValidation;
  if (moduleName === "DFSI Channel") return handleDFSIChannelsFileValidation;
  if (moduleName === "DFSI Group") return handleDFSIGroupsFileValidation;
  if (moduleName === "XSSI Connection")
    return handleXSSIConnectionFileValidation;
  return () => ({ finalFile: [], filewitherror: [] });
};

export const requestNewBodyforBulkUpload = {
  Location: function getRequestBodyForBulkUpload(entry: any) {
    return {
      "IWF-ID": entry["IWF-ID"],
      "IWF-LOCATION-NAME": entry["IWF-LOCATION-NAME"],
      "IWF-DOMAIN-NAME": entry["IWF-DOMAIN-NAME"],
    };
  },
  Organisation: function getRequestBodyForBulkUpload(entry: any) {
    return {
      "ORG-ID": entry["ORGANISATION-ID"],
      "ORG-NAME": entry["ORGANISATION-NAME"],
    };
  },
  "Organisation Translation": function getRequestBodyForBulkUpload(entry: any) {
    return {
      FROM: Number(entry["PRIORITY-INCOMING"]),
      EFI: entry["EMERGENCY-FLAG-INCOMING"].toLowerCase() === "true",
      EFO: entry["EMERGENCY-FLAG-OUTGOING"].toLowerCase() === "true",
      TO: Number(entry["PRIORITY-OUTGOING-VALUE"]),
    };
  },
  "Organisation Mapping": function getRequestBodyForBulkUpload(entry: any) {
    return {
      "IWF-ID": entry["IWF-ID"],
      "ORG-ID": entry["ORGANISATION-ID"],
    };
  },
  "OTAR Connection": function getRequestBodyForBulkUpload(entry: any) {
    return {
      "CONNECTION-ID": entry["CONNECTION-ID"],
      "KMF-IPV4-ADDR": entry["KMF-IPV4-ADDR"],
      "KMF-PORT": Number(entry["KMF-PORT"]),
      "KMF-RADIO-SET-IDENTIFIER": Number(entry["KMF-RADIO-SET-IDENTIFIER"]),
      "IWF-RADIO-SET-IDENTIFIER": Number(entry["IWF-RADIO-SET-IDENTIFIER"]),
      "MESSAGE-NUMBER-PERIOD": Number(entry["MESSAGE-NUMBER-PERIOD"]),
      "PRIVATE-CALL-SLN": Number(entry["PRIVATE-CALL-SLN"]),
      "GROUP-REGROUP-SLN": Number(entry["GROUP-REGROUP-SLN"]),
      "OTAR-REGISTRATION-TIMER": Number(entry["OTAR-REGISTRATION-TIMER"]),
      "OPT-SEND-REKEY-REQ":
        entry["OPT-SEND-REKEY-REQ"].toLowerCase() === "true",
      "OPT-REVERSE-WARM-START":
        entry["OPT-REVERSE-WARM-START"].toLowerCase() === "true",
      "OPT-DE-REGISTRATION":
        entry["OPT-DE-REGISTRATION"].toLowerCase() === "true",
      "OPT-HELLO-MAC-REPLY":
        entry["OPT-HELLO-MAC-REPLY"].toLowerCase() === "true",
      "OPT-KEY-ASSIGNMENT":
        entry["OPT-KEY-ASSIGNMENT"].toLowerCase() === "true",
      "KEEP-ALIVE-TIMER": Number(entry["KEEP-ALIVE-TIMER"]),
    };
  },
  "LMR User": function getRequestBodyForBulkUpload(entry: any) {
    return {
      "LMR-ID": entry["LMR-ID"],
      "LMR-NW-TYPE": entry["LMR-NW-TYPE"],
      "MAPPED-MCPTT-ID": entry["MAPPED-MCPTT-ID"],
      "DISPLAY-NAME": entry["DISPLAY-NAME"],
      "MUTUAL-AID": entry["MUTUAL-AID"].toLowerCase() === "true",
    };
  },
  "LMR Group": function getRequestBodyForBulkUpload(entry: any) {
    return {
      "TG-ID": entry["TG-ID"],
      "LMR-NW-TYPE": entry["LMR-NW-TYPE"],
      "MAPPED-MCPTT-GID": entry["MAPPED-MCPTT-GID"],
    };
  },
  "MCPTT User": function getRequestBodyForBulkUpload(entry: any) {
    return {
      "MCPTT-ID": entry["MCPTT-ID"],
      "DISPLAY-NAME": entry["DISPLAY-NAME"],
      ...(entry &&
        entry["MAPPED-P25-ID"] && {
          "MAPPED-P25-ID": entry["MAPPED-P25-ID"],
        }),
      ...(entry &&
        entry["MAPPED-DMR-ID"] && {
          "MAPPED-DMR-ID": entry["MAPPED-DMR-ID"],
        }),
      ...(entry &&
        entry["MAPPED-ROIP-ID"] && {
          "MAPPED-ROIP-ID": entry["MAPPED-ROIP-ID"],
        }),
      ...(entry &&
        entry["MAPPED-DFSI-ID"] && {
          "MAPPED-DFSI-ID": entry["MAPPED-DFSI-ID"],
        }),
      "MUTUAL-AID": entry["MUTUAL-AID"].toLowerCase() === "true",
    };
  },
  "DFSI Connection": function getRequestBodyForBulkUpload(entry: any) {
    return {
      "CONNECTION-ID": entry["CONNECTION-ID"],
      "DFSI-VERSION": Number(entry["DFSI-VERSION"]),
      "IWF-ROLE": entry["IWF-ROLE"],
      ...(entry["HOST-CONTROL-IPV4-ADDR"] && {
        "HOST-CONTROL-IPV4-ADDR": fetchHostControlIPV4Address(entry),
      }),
      ...(entry["HOST-CONTROL-PORT"] && {
        "HOST-CONTROL-PORT": Number(fetchHostControlPort(entry)),
      }),
      ...(entry["HOST-VOICE-IPV4-ADDR"] && {
        "HOST-VOICE-IPV4-ADDR": fetchHostVoiceIPV4Address(entry),
      }),
      "VOICE-PORT": Number(entry["VOICE-PORT"]),
      "FIXED-STATION-CONTROL-IPV4-ADDR":
        entry["FIXED-STATION-CONTROL-IPV4-ADDR"],
      "FIXED-STATION-CONTROL-PORT": Number(entry["FIXED-STATION-CONTROL-PORT"]),
      ...(entry["CONNECTIVITY-TX-TIMER"] && {
        "CONNECTIVITY-TX-TIMER": Number(fetchConnectivityTxTimerValue(entry)),
      }),
      "CONNECTIVITY-LOSS-LIMIT": Number(entry["CONNECTIVITY-LOSS-LIMIT"]),
      "CONTROL-RETRY-TIME": Number(entry["CONTROL-RETRY-TIME"]),
      "CONTROL-ATTEMPT-LIMIT": Number(entry["CONTROL-ATTEMPT-LIMIT"]),
      "ACKNOWLEDGEMENT-DELAY": Number(entry["ACKNOWLEDGEMENT-DELAY"]),
      ...(entry["FIXED-STATION-VOICE-IPV4-ADDR"] && {
        "FIXED-STATION-VOICE-IPV4-ADDR":
          fetchFixedStationVoiceIPV4Address(entry),
      }),
      ...(entry["FS-HEARTBEAT-PERIOD"] && {
        "FS-HEARTBEAT-PERIOD": Number(fetchFSHeartbeatPeriodValue(entry)),
      }),
      ...(entry["HOST-HEARTBEAT-PERIOD"] && {
        "HOST-HEARTBEAT-PERIOD": Number(fetchHostHeartbeatPeriodValue(entry)),
      }),
      ...(entry["DATA-PORT"] && {
        "DATA-PORT": Number(fetchDataPortValue(entry)),
      }),
      ...(entry["DATA-IPV4-ADDR"] && {
        "DATA-IPV4-ADDR": fetchDataIPV4AddressValue(entry),
      }),
      ...(entry["DFSI-VERSION"] &&
        +entry["DFSI-VERSION"] === 2 && {
          "OUTBOUND-MAX-CAI-DATA-BLOCKS":
            +entry["OUTBOUND-MAX-CAI-DATA-BLOCKS"],
        }),
      ...(entry["DFSI-VERSION"] &&
        +entry["DFSI-VERSION"] === 2 && {
          "INBOUND-MAX-CAI-DATA-BLOCKS": +entry["INBOUND-MAX-CAI-DATA-BLOCKS"],
        }),
      "INBOUND-REPEAT": entry["INBOUND-REPEAT"].toLowerCase() === "true",
      ...(entry["FIXED-STATION-VOTER-REPORTING-PERIOD"] && {
        "FIXED-STATION-VOTER-REPORTING-PERIOD": Number(
          fetchFixedStationVoterReportingPeriodValue(entry)
        ),
      }),
      ...(entry["FIXED-STATION-TRANSMITTER-REPORT-INTERVAL"] && {
        "FIXED-STATION-TRANSMITTER-REPORT-INTERVAL": Number(
          fetchFixedStationTransmitterReportIntervalValue(entry)
        ),
      }),
    };
  },
  "MCPTT Group": function getRequestBodyForBulkUpload(entry: any) {
    return {
      "MCPTT-GID": entry["MCPTT-GID"],
      ...(entry &&
        entry["MAPPED-P25-TG-ID"] && {
          "MAPPED-P25-TG-ID": entry["MAPPED-P25-TG-ID"],
        }),
      ...(entry &&
        entry["MAPPED-DMR-TG-ID"] && {
          "MAPPED-DMR-TG-ID": entry["MAPPED-DMR-TG-ID"],
        }),
      ...(entry &&
        entry["MAPPED-ROIP-TG-ID"] && {
          "MAPPED-ROIP-TG-ID": entry["MAPPED-ROIP-TG-ID"],
        }),
      ...(entry &&
        entry["MAPPED-DFSI-TG-ID"] && {
          "MAPPED-DFSI-TG-ID": entry["MAPPED-DFSI-TG-ID"],
        }),
    };
  },
  "DFSI Channel": function getRequestBodyForBulkUpload(entry: any) {
    return {
      "CONNECTION-ID": entry["CONNECTION-ID"],
      "CHANNEL-ID": entry["CHANNEL-ID"],
      "CHANNEL-MODE": entry["CHANNEL-MODE"],
      ...(entry["CHANNEL-MODE"] &&
        entry["CHANNEL-MODE"] === "Analog" &&
        parseInt(entry["DFSI-VERSION"], 10) === 2 &&
        handleBulkCTCnCDCvalues(
          "ctc",
          entry["CTC-SQUELCH-SYSTEM"],
          entry["CDC-SQUELCH-SYSTEM"],
          entry["CARRIER-SQUELCH"].toLowerCase() === "true"
        ) && {
          "CTC-SQUELCH-SYSTEM":
            entry["CARRIER-SQUELCH"].toLowerCase() === "true"
              ? 0
              : entry["CTC-SQUELCH-SYSTEM"] && +entry["CTC-SQUELCH-SYSTEM"],
        }),
      ...(entry["CHANNEL-MODE"] &&
        entry["CHANNEL-MODE"] === "Digital" && {
          "OTA-GROUP-ID": entry["OTA-GROUP-ID"] && +entry["OTA-GROUP-ID"],
        }),
      ...(entry["CHANNEL-MODE"] &&
        entry["CHANNEL-MODE"] === "Digital" && {
          "OUTBOUND-NETWORK-ACCESS-CODE":
            entry["OUTBOUND-NETWORK-ACCESS-CODE"] &&
            +entry["OUTBOUND-NETWORK-ACCESS-CODE"],
        }),
      ...(entry["CHANNEL-MODE"] &&
        entry["CHANNEL-MODE"] === "Digital" && {
          "INBOUND-NETWORK-ACCESS-CODE":
            entry["INBOUND-NETWORK-ACCESS-CODE"] &&
            +entry["INBOUND-NETWORK-ACCESS-CODE"],
        }),
      ...(entry["CHANNEL-MODE"] &&
        entry["CHANNEL-MODE"] === "Analog" &&
        parseInt(entry["DFSI-VERSION"], 10) === 2 &&
        handleBulkCTCnCDCvalues(
          "cdc",
          entry["CTC-SQUELCH-SYSTEM"],
          entry["CDC-SQUELCH-SYSTEM"],
          entry["CARRIER-SQUELCH"].toLowerCase() === "true"
        ) && {
          "CDC-SQUELCH-SYSTEM":
            entry["CARRIER-SQUELCH"].toLowerCase() === "true"
              ? 0
              : entry["CDC-SQUELCH-SYSTEM"] && +entry["CDC-SQUELCH-SYSTEM"],
        }),
    };
  },
  "DFSI Group": function getRequestBodyForBulkUpload(
    entry: any,
    org?: string,
    iwfID?: string
  ) {
    return {
      "IWF-ID": iwfID,
      "DFSI-LOGICAL-GID":
        entry.FORMAT === "FreeForm"
          ? entry["DFSI-LOGICAL-GID"]
          : `${org}-${iwfID}${entry["DFSI-LOGICAL-GID"].substring(2)}`,
      "CHANNEL-IDS": entry["CHANNEL-IDS"].split(",").map((id: any) => ({
        "CHANNEL-ID": id.trim(),
      })),
      "DFSI-GROUP-NAME": entry["DFSI-GROUP-NAME"],
      "ENCRYPTION-ENABLED":
        entry["ENCRYPTION-ENABLED"].toLowerCase() === "true",
      ...(entry &&
        ["true", "false"].includes(entry["ENCRYPTION-ENABLED"].toLowerCase()) &&
        !Number.isNaN(parseInt(entry["STORAGE-LOCATION-NUMBER"], 10)) && {
          SLN: Number(entry["STORAGE-LOCATION-NUMBER"]),
        }),
    };
  },
  "Canned Message": function getRequestBodyForBulkUpload(entry: any) {
    return {
      "MSG-ID": entry["MESSAGE-ID"],
      "CANNED-TEXT": entry["CANNED-TEXT"],
    };
  },
  "XSSI Connection": function getRequestBodyForBulkUpload(entry: any) {
    return {
      "CONNECTION-ID": entry["CONNECTION-ID"],
      "RADIO-IPV4-ADDR": entry["RADIO-IPV4-ADDR"],
      "RADIO-RFSS-ID": Number(entry["RADIO-RFSS-ID"]),
      "RADIO-SYSTEM-ID": Number(entry["RADIO-SYSTEM-ID"]),
      "RADIO-WACN-ID": Number(entry["RADIO-WACN-ID"]),
      "RADIO-RFSS-4-HEX-DIGIT": entry["RADIO-RFSS-4-HEX-DIGIT"],
      "IWF-RFSS-ID": Number(entry["IWF-RFSS-ID"]),
      "IWF-SYSTEM-ID": Number(entry["IWF-SYSTEM-ID"]),
      "IWF-WACN-ID": Number(entry["IWF-WACN-ID"]),
      "RESPONSES-BEFORE-CONNECTED": Number(entry["RESPONSES-BEFORE-CONNECTED"]),
      "MISSED-RESPONSES-BEFORE-DISCONNECTED": Number(
        entry["MISSED-RESPONSES-BEFORE-DISCONNECTED"]
      ),
      "VOCODER-MODES": entry["VOCODER-MODES"].split(","),
      "POLLING-ENABLED": entry["POLLING-ENABLED"],
      "POLLING-INTERVAL-CONNECTED": Number(entry["POLLING-INTERVAL-CONNECTED"]),
      "POLLING-INTERVAL-DISCONNECTED": Number(
        entry["POLLING-INTERVAL-DISCONNECTED"]
      ),
      "IWF-GROUP-RANGES": entry["IWF-GROUP-RANGES"],
      "UNIT-RANGES": entry["UNIT-RANGES"],
      "RADIO-GROUP-RANGES": entry["RADIO-GROUP-RANGES"],
    };
  },
};
